import { z } from 'zod';
import { siteModelSchema } from './project-file-site-model.model';
import {
  projectFileExtractionOutputMergedSchema,
  type ProjectFileExtractionOutput,
} from './project-file-extraction.model';
import { buildingPartRequirementAggregatedSchema } from './building-part-requirement.model';
import {
  extractedAreaTotalsSchema,
  extractedDesignFactConflictSchema,
  extractedDesignFactsSchema,
} from './extracted-design-facts.model';
import { ffuOutputFactsSchema } from './ffu-output.model';
import {
  extractedDrawingSchema,
  projectFilePageRasterSchema,
} from './project-file-drawing.model';
import { projectFileAiCallSchema } from './project-file-ai-call.model';
import { storeyLevelSchema } from './storey-level.model';
import type { ProjectFileAiCost } from '../helpers/project-file-pipeline-ai.helpers';

// ---------------------------------------------------------------------------
// State enums — must mirror the CHECK constraints in
// 20260427120000-project-file-batches-tables-up.sql.
// ---------------------------------------------------------------------------

export const projectFileBatchStateSchema = z.enum([
  'collecting',
  'classifying',
  'awaiting_confirmation',
  'extracting',
  'consolidating',
  'done',
  'interrupted',
  'failed',
]);
export type ProjectFileBatchState = z.infer<typeof projectFileBatchStateSchema>;

export const PROJECT_FILE_BATCH_TERMINAL_STATES = ['done', 'failed'] as const;

export const projectFileStateSchema = z.enum([
  'queued',
  'preprocessing',
  'classifying',
  'classified',
  'extracting',
  'done',
  'failed',
  'cancelled',
]);
export type ProjectFileState = z.infer<typeof projectFileStateSchema>;

export const PROJECT_FILE_TERMINAL_STATES = [
  'done',
  'failed',
  'cancelled',
] as const;

// ---------------------------------------------------------------------------
// Classifications — a file may include multiple segment `type` values at once
// (e.g. a PDF that is both blueprint and project_description). The
// classifier emits one entry per applicable type; multiple blueprint rows can
// reference the same physical building via repeated `buildingInstanceId`.
// The extractor consumes all rows to produce one `extraction_output` (versioned).
// ---------------------------------------------------------------------------

export const projectFileClassificationTypeSchema = z.enum([
  'blueprint',
  'building_rendering',
  'project_description',
  'project_brief',
  'area_schedule',
  'bill_of_materials',
  'cost_estimate',
  'technical_spec',
  'title_block',
  'unknown',
]);
export type ProjectFileClassificationType = z.infer<
  typeof projectFileClassificationTypeSchema
>;

/**
 * Per-blueprint-segment subtype. Populated only when {@link
 * projectFileClassificationTypeSchema} === `blueprint`.
 *
 * - `top_view`: drawings looking DOWN at a horizontal plane (entréplan,
 *   bottenplan, normalplan, översta plan, källarplan/garageplan, gårdsplan,
 *   takplan). Reveal storey footprint and per-storey masses.
 * - `side_view`: drawings cut or seen from the SIDE (sektion, fasad). Reveal
 *   storey count, storey heights, total height, vertical mass relationships,
 *   and continuity of floors/slabs across labelled buildings.
 *
 * Site plans (situationsplan / nybyggnadskarta) are already captured at the
 * file level by `document_kind = site_plan_landscape` and intentionally do
 * NOT get a blueprint subtype.
 */
export const blueprintSubtypeSchema = z.enum(['top_view', 'side_view']);
export type BlueprintSubtype = z.infer<typeof blueprintSubtypeSchema>;

export const BLUEPRINT_SUBTYPE_AI_GUIDE = {
  top_view: [
    "Plan view looking down at one horizontal level — shows that level's footprint, walls, and any masses on that level.",
    'SV: planritning, entréplan, bottenplan, normalplan, plan N, översta plan, källarplan, garageplan, gårdsplan, takplan.',
  ],
  side_view: [
    'Vertical drawing — section through the building or external elevation. Shows storey count, heights, vertical relationships, roof form.',
    'SV: sektion (sektion A–A), fasad / fasadritning.',
  ],
} as const satisfies Record<BlueprintSubtype, readonly [string, string]>;

export const blueprintSubtypeGuidePromptLines = (
  s: BlueprintSubtype,
): string => {
  const [en, sv] = BLUEPRINT_SUBTYPE_AI_GUIDE[s];
  return `${en}\n    ${sv}`;
};

/**
 * Whole-file document kind — one label per upload, distinct from per-segment
 * {@link projectFileClassificationTypeSchema}. Passed to extractors with the filename.
 */
export const projectFileDocumentKindSchema = z.enum([
  'technical_drawing_set',
  'cover_title_register',
  'written_technical_spec',
  'project_description_prose',
  'area_room_schedule',
  'bill_of_quantities',
  'cost_estimate_budget',
  'visualization_rendering',
  'site_plan_landscape',
  'spreadsheet_table_data',
  'contract_legal',
  'photo_image_survey',
  'energy_sustainability_report',
  'mixed_project_bundle',
  'unknown_other',
]);
export type ProjectFileDocumentKind = z.infer<
  typeof projectFileDocumentKindSchema
>;

/** Per kind: [English gloss, Swedish industry keywords]. */
export const PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE = {
  technical_drawing_set: [
    'Primary content is measured CAD/drawings: plans, sections, elevations, details (often a multi-sheet PDF).',
    'SV: ritning(ar), plan-, sektions-, fasadritning, detaljritning, stomritning, A-/S-serie.',
  ],
  cover_title_register: [
    'Cover sheet, title block as main content, drawing index / bladlista, revision table — little substantive drawing body.',
    'SV: försättsblad, täckblad, blad- och ritningsförteckning, titelblock som huvudinnehåll.',
  ],
  written_technical_spec: [
    'Written requirements and execution text (not graphic sheets).',
    'SV: teknisk beskrivning, utförande beskrivning, prestanda/AMA/BBK-hänvisningar.',
  ],
  project_description_prose: [
    'Narrative project scope, background, design intent (prose).',
    'SV: projektbeskrivning, förstudie, beskrivande sammanfattning.',
  ],
  area_room_schedule: [
    'Room/area tables: BTA, LOA, BRA, room lists.',
    'SV: areatabell, rumsförteckning, ytor, ytsammanställning.',
  ],
  bill_of_quantities: [
    'Quantities, articles, materials lists (BOM-style).',
    'SV: mängdförteckning, materiallista, produktlista.',
  ],
  cost_estimate_budget: [
    'Costs, budgets, tenders, price breakdowns.',
    'SV: kostnadskalkyl, budget, anbud, prisuppgift, lönsamhetskalkyl.',
  ],
  visualization_rendering: [
    'CGI / marketing imagery; low CAD fidelity.',
    'SV: visualisering, arkitektrendering, illustrationsbild.',
  ],
  site_plan_landscape: [
    'Site layout, terrain, landscaping (plan form).',
    'SV: situationsplan, markplan, tomtkarta, landskap.',
  ],
  spreadsheet_table_data: [
    'Spreadsheet-native or table-heavy export (Excel-like density).',
    'SV: Excel-, tabell- eller registerflöde (data i rader/kolumner).',
  ],
  contract_legal: [
    'Contract, agreement, legal or procurement annex.',
    'SV: avtal, kontrakt, upphandlingsbilaga, juridisk bilaga.',
  ],
  photo_image_survey: [
    'Photos, image-only surveys, appendix of pictures.',
    'SV: foto, bildbilaga, inventeringsfoto.',
  ],
  energy_sustainability_report: [
    'Energy, climate, sustainability declarations.',
    'SV: energiberäkning, energideklaration, LCA, miljö-/klimatredovisning.',
  ],
  mixed_project_bundle: [
    'Clearly mixed disciplines or formats in one file (no single dominant kind).',
    'SV: samlings-PDF med flera innehållstyper.',
  ],
  unknown_other: [
    'Does not fit the list or unreadable.',
    'SV: oklassificerbar / övrigt.',
  ],
} as const satisfies Record<ProjectFileDocumentKind, readonly [string, string]>;

export const projectFileDocumentKindGuidePromptLines = (
  k: ProjectFileDocumentKind,
): string => {
  const [en, sv] = PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE[k];
  return `${en}\n    ${sv}`;
};

/**
 * Per type: [English gloss, Swedish industry keywords]. `type` output stays English snake_case;
 * `evidenceExcerpt`/`notes` may quote Swedish from the file when useful.
 */
export const PROJECT_FILE_CLASSIFICATION_TYPE_AI_GUIDE = {
  blueprint: [
    'Measured technical CAD/drawing content: plans, sections, elevations, details, layouts.',
    'SV: ritning(ar), situationsplan, plan/sektions/fasadritning, stom- och detaljritning.',
  ],
  building_rendering: [
    'Non-CAD visuals: CGI; lower geometric fidelity than blueprint sheets.',
    'SV: visualisering, tävlings-/säljbilder, illustrationsrender, arkitektrendering.',
  ],
  project_description: [
    'Substantive prose on scope, programme, design narrative, background — not only a one-pager.',
    'SV: projektbeskrivning, beskrivande text, förklarande avsnitt.',
  ],
  project_brief: [
    'Early-phase charter or mandate (often short).',
    'SV: förstudie, förfrågan/behov, uppdrags-/beställarunderlag, mål och ramar.',
  ],
  area_schedule: [
    'Space/area tables: rooms, areas, zones, GFA-style takeoffs.',
    'SV: areatabell, rumsförteckning, ytor (BTA/LOA/BRA där relevant).',
  ],
  bill_of_materials: [
    'Bill-of-materials style lists: quantities, articles, products, materials (common abbreviation: BOM).',
    'SV: mängdförteckning, materiallista, produkt-/artikellista.',
  ],
  cost_estimate: [
    'Money side: budgets, tenders, price breakdowns.',
    'SV: kostnadskalkyl, budget, anbudsunderlag, lönsamhetskalkyl.',
  ],
  technical_spec: [
    'Written performance/quality/execution requirements, standards text — not graphic-only sheets.',
    'SV: teknisk beskrivning, prestanda-/utförandekrav, hänvisning AMA/BBK där förekommande.',
  ],
  title_block: [
    'Sheet dominated by cover, index, or title block / drawing stamp — little substantive drawing body or running text (not a normal plotted sheet that happens to have a stamp). Downstream: optional filing metadata only (drawing id, revision, project/parties); not schedules, quantities, or geometry.',
    'SV: försättsblad, täckblad, titelblock/ritningsstämpel som bär huvudinnehållet, innehållsförteckning utan egentlig ritning.',
  ],
  unknown: [
    'Cannot map to a type or unusable scan.',
    'SV: oklassificerbar / oläsbar.',
  ],
} as const satisfies Record<
  ProjectFileClassificationType,
  readonly [string, string]
>;

/** English line + indented Swedish line for classifier prompt bullets. */
export const projectFileClassificationGuidePromptLines = (
  t: ProjectFileClassificationType,
): string => {
  const [en, sv] = PROJECT_FILE_CLASSIFICATION_TYPE_AI_GUIDE[t];
  return `${en}\n    ${sv}`;
};

export const projectFileClassificationSchema = z.object({
  type: projectFileClassificationTypeSchema,
  /**
   * Required when {@link type} === `blueprint`, null otherwise.
   * See {@link blueprintSubtypeSchema}.
   */
  blueprintSubtype: z.union([blueprintSubtypeSchema, z.null()]).optional(),
  confidence: z.number().min(0).max(1),
  confirmed: z.boolean(),
  /** Verbatim or near-verbatim lines from the document that support this segment’s type. */
  evidenceExcerpt: z.string().optional(),
  notes: z.string().optional(),
  // Optional page-range hint (`page_hint`) so the extractor can narrow focus;
  // not a structural divider.
  pageHint: z.string().optional(),
  /**
   * 1-based pages of the file this segment is on. A call that reads only some
   * segments is sent only these pages. Absent on older rows and on images.
   */
  pages: z.array(z.number().int().positive()).optional(),
  /**
   * Stable key within this file for one physical structure. Re-use the SAME id on every segment
   * (e.g. multiple blueprint sheets) that still describe one building; use a DIFFERENT id per
   * distinct building when the document clearly separates them.
   */
  buildingInstanceId: z.string().optional(),
  /**
   * Every building this segment covers. One entry when the segment is about
   * one building; several when one sheet shows several. Absent on older rows
   * — readers fall back to {@link buildingInstanceId}.
   */
  buildingInstanceIds: z.array(z.string()).optional(),
  /** Human label from stamps / table (e.g. "Hus B", building number). Optional supplement to {@link buildingInstanceId}. */
  buildingLabel: z.string().optional(),
  /**
   * Stamp fields the classifier read on a `title_block` segment. Extraction
   * uses them instead of a separate call; absent on older rows.
   */
  titleBlock: z
    .object({
      drawingId: z.string().nullable(),
      revision: z.string().nullable(),
      projectTitle: z.string().nullable(),
    })
    .optional(),
});
export type ProjectFileClassification = z.infer<
  typeof projectFileClassificationSchema
>;

/** Who produced a segment; repeated on each row so multiple model runs can be concatenated. */
export const projectFileClassifierMetaSchema = z.object({
  llmProvider: z.string(),
  model: z.string(),
  promptVersion: z.string().optional(),
  runId: z.string().optional(),
});
export type ProjectFileClassifierMeta = z.infer<
  typeof projectFileClassifierMetaSchema
>;

export const projectFileClassificationEnvelopeSchema = z.object({
  classifier: projectFileClassifierMetaSchema,
  data: projectFileClassificationSchema,
});
export type ProjectFileClassificationEnvelope = z.infer<
  typeof projectFileClassificationEnvelopeSchema
>;

/** Legacy rows stored flat {@link ProjectFileClassification}; normalize for reads. */
const LEGACY_CLASSIFIER_STUB: ProjectFileClassifierMeta = {
  llmProvider: 'legacy',
  model: 'unknown',
};

/** Single stored segment → envelope (accepts legacy flat shape). */
export const normalizeProjectFileClassificationStored = (
  raw: unknown,
): ProjectFileClassificationEnvelope => {
  const env = projectFileClassificationEnvelopeSchema.safeParse(raw);
  if (env.success) return env.data;
  const flat = projectFileClassificationSchema.safeParse(raw);
  if (flat.success)
    return { classifier: LEGACY_CLASSIFIER_STUB, data: flat.data };
  throw new Error(
    `Invalid stored classification segment: ${JSON.stringify(raw)}`,
  );
};

/** Optional whole-document hints stored inside `project_files.classifications` JSON (with `segments`). */
export const classifierDocumentLabelsSchema = z.object({
  classifierSuggestedProjectName: z.union([z.string(), z.null()]),
  classifierSuggestedProjectDescription: z.union([z.string(), z.null()]),
  classifierDocumentKind: z
    .union([projectFileDocumentKindSchema, z.null()])
    .optional(),
  classifierDocumentKindConfidence: z
    .number()
    .min(0)
    .max(1)
    .nullable()
    .optional(),
});
export type ClassifierDocumentLabels = z.infer<
  typeof classifierDocumentLabelsSchema
>;

const trimNullable = (v: string | null): string | null => {
  if (v === null) return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
};

/**
 * Reads {@link classifierDocumentLabelsSchema} when `classifications` is the
 * `{ segments, classifierDocumentLabels }` wrapper; legacy plain arrays yield nulls.
 */
export const parseClassifierDocumentLabelsFromStored = (
  classificationsRaw: unknown,
): ClassifierDocumentLabels => {
  const empty: ClassifierDocumentLabels = {
    classifierSuggestedProjectName: null,
    classifierSuggestedProjectDescription: null,
    classifierDocumentKind: null,
    classifierDocumentKindConfidence: null,
  };
  if (
    classificationsRaw == null ||
    typeof classificationsRaw !== 'object' ||
    Array.isArray(classificationsRaw)
  ) {
    return empty;
  }
  const o = classificationsRaw as Record<string, unknown>;
  const raw = o.classifierDocumentLabels;
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw))
    return empty;
  const p = classifierDocumentLabelsSchema.safeParse(raw);
  if (!p.success) return empty;
  const confRaw = p.data.classifierDocumentKindConfidence;
  const conf =
    confRaw == null || typeof confRaw !== 'number' || Number.isNaN(confRaw)
      ? null
      : Math.min(1, Math.max(0, confRaw));

  return {
    classifierSuggestedProjectName: trimNullable(
      p.data.classifierSuggestedProjectName,
    ),
    classifierSuggestedProjectDescription: trimNullable(
      p.data.classifierSuggestedProjectDescription,
    ),
    classifierDocumentKind: p.data.classifierDocumentKind ?? null,
    classifierDocumentKindConfidence: conf,
  };
};

/** Segments array: legacy `[...]` or wrapper `{ segments: [...] }`. */
export const extractClassificationSegmentsStored = (
  raw: unknown,
): unknown[] => {
  if (Array.isArray(raw)) return raw;
  if (raw !== null && typeof raw === 'object' && !Array.isArray(raw)) {
    const segments = (raw as { segments?: unknown }).segments;
    if (Array.isArray(segments)) return segments;
  }
  throw new Error(
    'project_files.classifications must be a JSON array or { segments } wrapper',
  );
};

/** Persisted `classifications` JSON — segment array only, or wrapper with document labels. */
export const buildClassificationsStoragePayload = (
  segments: readonly ProjectFileClassificationEnvelope[],
  documentLabels?: ClassifierDocumentLabels,
): unknown =>
  documentLabels !== undefined
    ? {
        segments: [...segments],
        classifierDocumentLabels: documentLabels,
      }
    : [...segments];

/** Whole JSONB array from `project_files.classifications`. */
export const normalizeProjectFileClassificationsStored = (
  raw: unknown,
): ProjectFileClassificationEnvelope[] =>
  extractClassificationSegmentsStored(raw).map(
    normalizeProjectFileClassificationStored,
  );

export const projectFileExtractionOutputSchema =
  projectFileExtractionOutputMergedSchema;
export type { ProjectFileExtractionOutput };

// ---------------------------------------------------------------------------
// File — one uploaded artifact within a batch.
// ---------------------------------------------------------------------------

export const projectFileSchema = z.object({
  id: z.string(),
  batchId: z.string(),
  projectId: z.number().nullable(),
  organizationId: z.string(),
  fileName: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number().nullable(),
  blobRef: z.string().nullable(),
  extractedDrawings: z.array(extractedDrawingSchema).default([]),
  /** One raster per page. Drawings on that page store coordinates, not their own image. */
  pageRasters: z.array(projectFilePageRasterSchema).default([]),
  state: projectFileStateSchema,
  classifications: z.array(projectFileClassificationEnvelopeSchema),
  extractionOutput: projectFileExtractionOutputSchema.nullable(),
  model: z.string().nullable(),
  promptVersion: z.string().nullable(),
  classifierSuggestedProjectName: z.union([z.string(), z.null()]),
  classifierSuggestedProjectDescription: z.union([z.string(), z.null()]),
  classifierDocumentKind: z.union([projectFileDocumentKindSchema, z.null()]),
  classifierDocumentKindConfidence: z.number().min(0).max(1).nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectFile = z.infer<typeof projectFileSchema>;

// ---------------------------------------------------------------------------
// Aggregated output — populated by joint analysis (phase 6). Phase 1 ships an
// open-ended record so the runner can write stub values without a schema lock.
// Tightened in phase 5/6.
// ---------------------------------------------------------------------------

/** One physical building after batch consolidation — human-readable briefing text (no tooling jargon). */
export const projectFileBatchConsolidatedBuildingSchema = z.object({
  /** Aligns with classifier `buildingInstanceId` / blueprint `building_instance_key` when known. */
  buildingInstanceKey: z.string().nullable(),
  buildingLabel: z.string().nullable(),
  /** Plain language for people and downstream steps; exclude tool/control names and tech identifiers in stored text. */
  designAiDescription: z.string(),
});
export type ProjectFileBatchConsolidatedBuilding = z.infer<
  typeof projectFileBatchConsolidatedBuildingSchema
>;

/**
 * Cross-building composition row — flags cases where the documents label
 * several "hus" but they share a continuous base slab / connector volume
 * and should be modelled as a single nodon building downstream.
 */
export const projectFileBatchBuildingCompositionRelationshipSchema = z.enum([
  'shared_base_slab',
  'connected_volume',
  'separate',
  'unknown',
]);
export type ProjectFileBatchBuildingCompositionRelationship = z.infer<
  typeof projectFileBatchBuildingCompositionRelationshipSchema
>;

export const projectFileBatchBuildingCompositionSchema = z.object({
  /** References to `consolidatedBuildings[].buildingInstanceKey`. */
  composingBuildingKeys: z.array(z.string()),
  relationship: projectFileBatchBuildingCompositionRelationshipSchema,
  /** Short prose sentence in source language explaining the composition. */
  compositionNote: z.string(),
  /** When true, downstream design AI should collapse the composing buildings into one. */
  shouldModelAsSingleBuilding: z.boolean(),
  /** Totals of the composed body. Null on rows stored before the field. */
  areas: extractedAreaTotalsSchema.nullable().default(null),
});
export type ProjectFileBatchBuildingComposition = z.infer<
  typeof projectFileBatchBuildingCompositionSchema
>;

export const STOREY_STACK_EVIDENCE_KINDS = [
  'section',
  'elevation',
  'plan',
  'area_table',
] as const;

const storeyStackEvidenceKindSchema = z.enum(STOREY_STACK_EVIDENCE_KINDS);

/**
 * One floor plate of a building as the joined extraction read it from the
 * building's sections and plans together: every printed name the floor has,
 * the levels inside it that are not storeys, and the plan drawings that show
 * it.
 */
const projectFileBatchStoreyStackRowSchema = z.object({
  partName: z.string().nullable(),
  /** Null when the joined extraction could not place the floor. */
  level: storeyLevelSchema.nullable(),
  names: z.array(z.string()),
  planNumbers: z.array(z.number().int()),
  floorLevelM: z.number().nullable(),
  /** Printed levels inside this storey (landing, half level), not storeys. */
  levelsWithinM: z.array(z.number()),
  /** {@link ExtractedDrawing} ids of the plan sheets that show this floor. */
  drawingIds: z.array(z.string()),
  evidence: z.array(
    z.object({
      kind: storeyStackEvidenceKindSchema,
      fileName: z.string(),
      page: z.number().int().nullable(),
    }),
  ),
});
export type ProjectFileBatchStoreyStackRow = z.infer<
  typeof projectFileBatchStoreyStackRowSchema
>;

/**
 * Joined blueprint extraction — one volumetric AI call per building group
 * across files, focused on overall building volume rather than precise
 * per-storey numbers (see `project-file-blueprint-joined-extraction*`).
 */
export const projectFileBatchJoinedBlueprintExtractionSchema = z.object({
  /** Echo of classifier `buildingInstanceId`; null for the ungrouped group. */
  buildingInstanceKey: z.string().nullable(),
  buildingLabel: z.string().nullable(),
  sourceFileNames: z.array(z.string()),
  accurateDescription: z.string(),
  overallHeightM: z.number().nullable(),
  storeyCount: z.number().nullable(),
  /** Bottom to top. Empty on batches read before the stack existed. */
  storeyStack: z.array(projectFileBatchStoreyStackRowSchema).default([]),
  massCompositionNote: z.string().nullable(),
  /**
   * Self-reported adjacency notes — neighbouring buildings visible in this
   * group's own drawings (site plan, joint section). Lets the merger detect
   * shared-base compositions even though each joined call only sees its own
   * group's files.
   */
  physicalAttachments: z
    .array(
      z.object({
        otherBuildingLabel: z.string(),
        sharedStructure: z.string(),
        shouldModelAsSingleBuilding: z.boolean(),
      }),
    )
    .default([]),
});
export type ProjectFileBatchJoinedBlueprintExtraction = z.infer<
  typeof projectFileBatchJoinedBlueprintExtractionSchema
>;

export const projectFileBatchAggregatedOutputSchema = z
  .record(z.string(), z.unknown())
  .and(
    z.object({
      sourceFileIds: z.array(z.string()).optional(),
      conflicts: z.array(extractedDesignFactConflictSchema).optional(),
      /** Batch-level merge of per-file classifier + extraction hints (consolidation stage). */
      suggestedProjectName: z.string().nullable().optional(),
      suggestedProjectDescription: z.string().nullable().optional(),
      /** One entry per distinct building mass; produced in consolidation from blueprint extractions. */
      consolidatedBuildings: z
        .array(projectFileBatchConsolidatedBuildingSchema)
        .optional(),
      /** Cross-building composition layer (shared base slabs, connector volumes); empty array when every building stands separate. */
      buildingCompositions: z
        .array(projectFileBatchBuildingCompositionSchema)
        .optional(),
      /** One row per blueprint building group joined across files (overall volume focus). */
      joinedBlueprintExtractions: z
        .array(projectFileBatchJoinedBlueprintExtractionSchema)
        .optional(),
      /** One row per group whose joined extraction call failed; surfaced for diagnostics. */
      joinedBlueprintExtractionFailures: z
        .array(
          z.object({
            groupKey: z.string().nullable(),
            fileCount: z.number(),
            reason: z.string(),
          }),
        )
        .optional(),
      /** Deduped FFU building-part requirements collected from segment extractions. */
      buildingPartRequirements: z
        .array(buildingPartRequirementAggregatedSchema)
        .optional(),
      /**
       * Resolved design facts. New batches store the scorer shape.
       * Older batches store the working tree. The scorer shape is tried first.
       */
      extractedDesignFacts: z
        .union([ffuOutputFactsSchema, extractedDesignFactsSchema])
        .optional(),
      /**
       * Merge-model extracted tree before coalesce with heuristic attribution.
       * Enables post-AI replay of consolidation without re-running merge AI.
       */
      consolidationMergeExtractedDesignFacts:
        extractedDesignFactsSchema.optional(),
      /** Merge-model totals for several buildings that are not one building. */
      consolidationMergeProjectAreas: extractedAreaTotalsSchema
        .nullable()
        .optional(),
    }),
  );
export type ProjectFileBatchAggregatedOutput = z.infer<
  typeof projectFileBatchAggregatedOutputSchema
>;

// ---------------------------------------------------------------------------
// Timeline — append-only step log. One entry when a state is entered
// (`started`), one when it is transitioned out of (`completed`). Used by the
// admin UI to show stage durations and the WS-driven activity feed.
// ---------------------------------------------------------------------------

const projectFileBatchTimelineEventSchema = z.enum(['started', 'completed']);

export const projectFileBatchTimelineBatchEntrySchema = z.object({
  at: z.string(),
  scope: z.literal('batch'),
  state: projectFileBatchStateSchema,
  event: projectFileBatchTimelineEventSchema,
});

export const projectFileBatchTimelineFileEntrySchema = z.object({
  at: z.string(),
  scope: z.literal('file'),
  state: projectFileStateSchema,
  event: projectFileBatchTimelineEventSchema,
  fileId: z.string(),
});

export const projectFileBatchTimelineEntrySchema = z.discriminatedUnion(
  'scope',
  [
    projectFileBatchTimelineBatchEntrySchema,
    projectFileBatchTimelineFileEntrySchema,
  ],
);
export type ProjectFileBatchTimelineEntry = z.infer<
  typeof projectFileBatchTimelineEntrySchema
>;

// ---------------------------------------------------------------------------
// Batch — one analysis run owned by a user, optionally attached to a project.
// ---------------------------------------------------------------------------

export const projectFileBatchSchema = z.object({
  id: z.string(),
  userId: z.string(),
  organizationId: z.string(),
  projectId: z.number().nullable(),
  state: projectFileBatchStateSchema,
  /**
   * Pipe that produced this batch. Absent or null on rows created before
   * pipes were recorded; those runs are the existing FFU pipe (`ffu`).
   */
  pipeId: z.string().nullable().optional(),
  /** Model chosen when a single-call run started. Null uses the consolidation default. */
  model: z.string().nullable().optional(),
  aggregatedOutput: projectFileBatchAggregatedOutputSchema.nullable(),
  /**
   * Final output: the consolidated output (`aggregatedOutput.extractedDesignFacts`)
   * after the deterministic normalisations, e.g. a house described in parts is
   * one building. Null before the batch is done and on older rows.
   */
  finalOutput: ffuOutputFactsSchema.nullable().optional(),
  /**
   * Every building and storey read from all plan pages at once, in one frame.
   * Null before the step ran, when it failed, and on older rows.
   */
  siteModel: siteModelSchema.nullable().optional(),
  /** FFU baseline POC: create a project from the final output when done. */
  createProject: z.boolean().optional(),
  timeline: z.array(projectFileBatchTimelineEntrySchema),
  /** Append-only log of every model call the pipeline made for this batch. */
  aiCalls: z.array(projectFileAiCallSchema).default([]),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type ProjectFileBatch = z.infer<typeof projectFileBatchSchema>;

// Snapshot returned by the polling endpoint: batch + every file (with sub-docs).
export const projectFileBatchSnapshotSchema = z.object({
  batch: projectFileBatchSchema,
  files: z.array(projectFileSchema),
  /** Set on admin GET only; omit on regular batch snapshot + socket payloads. */
  batchUserDisplayName: z.string().nullable().optional(),
});
export type ProjectFileBatchSnapshot = z.infer<
  typeof projectFileBatchSnapshotSchema
>;

// Row returned by the admin cross-org listing — batch metadata + file-state
// rollup so the list view doesn't have to fetch every file separately. The
// outputs, site model, timeline and AI calls stay out: the list shows none of
// them, and they make up most of a batch.
export type ProjectFileBatchAdminRow = Pick<
  ProjectFileBatch,
  | 'id'
  | 'userId'
  | 'organizationId'
  | 'projectId'
  | 'state'
  | 'pipeId'
  | 'model'
  | 'failureReason'
  | 'createdAt'
  | 'updatedAt'
> & {
  aiCost: ProjectFileAiCost;
  fileCounts: { total: number; done: number; failed: number };
  /** From extraction segments (title block / project description); not persisted on batch row. */
  suggestedProjectName: string | null;
  suggestedProjectDescription: string | null;
  /** Resolved via Auth0 profile; null if lookup failed. */
  userDisplayName: string | null;
};

// ---------------------------------------------------------------------------
// Transition guards — single source of truth for legal state moves. Used by
// the repository (DB writes) and the service (runner dispatch).
// ---------------------------------------------------------------------------

export const PROJECT_FILE_BATCH_TRANSITIONS: Record<
  ProjectFileBatchState,
  readonly ProjectFileBatchState[]
> = {
  collecting: ['classifying', 'consolidating', 'interrupted', 'failed'],
  classifying: ['awaiting_confirmation', 'interrupted', 'failed'],
  awaiting_confirmation: ['extracting', 'interrupted', 'failed'],
  extracting: ['consolidating', 'interrupted', 'failed'],
  consolidating: ['done', 'failed'],
  interrupted: ['collecting'],
  done: [],
  failed: [],
};

export const PROJECT_FILE_TRANSITIONS: Record<
  ProjectFileState,
  readonly ProjectFileState[]
> = {
  queued: ['preprocessing', 'done', 'cancelled', 'failed'],
  preprocessing: ['classifying', 'failed'],
  classifying: ['classified', 'failed'],
  classified: ['extracting', 'cancelled', 'failed'],
  extracting: ['done', 'failed'],
  done: [],
  failed: [],
  cancelled: [],
};

export const canTransitionBatch = (
  from: ProjectFileBatchState,
  to: ProjectFileBatchState,
): boolean => PROJECT_FILE_BATCH_TRANSITIONS[from].includes(to);

export const canTransitionFile = (
  from: ProjectFileState,
  to: ProjectFileState,
): boolean => PROJECT_FILE_TRANSITIONS[from].includes(to);

export const isTerminalBatchState = (s: ProjectFileBatchState): boolean =>
  (PROJECT_FILE_BATCH_TERMINAL_STATES as readonly string[]).includes(s);

export const isTerminalFileState = (s: ProjectFileState): boolean =>
  (PROJECT_FILE_TERMINAL_STATES as readonly string[]).includes(s);

export const isProjectFileBatchSnapshot = (
  v: unknown,
): v is ProjectFileBatchSnapshot =>
  projectFileBatchSnapshotSchema.safeParse(v).success;
